import React, { useState, Fragment, useCallback, useEffect } from 'react';
import { translate } from '../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import { reloadPayment } from '../Actions/Checkout.action';
import { update, loadError } from '../Actions/Cart.action';
import * as debounce from 'lodash.debounce';
import { catchError } from '../Actions/Error.action';
import { fetchBudget, validateCheckout} from '../Actions/WorkWear.action'

const mapCartToAbort = {};

const quantityDispatch = debounce((articleNumber, quantity, dispatch) => {
    mapCartToAbort[articleNumber] = new AbortController();
    dispatch(update(articleNumber, quantity, mapCartToAbort[articleNumber]))
        .then(() => dispatch(reloadPayment()))
        .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
}, 500);

const Cart = () => {
    const dispatch = useDispatch();

    const [removingRow, setRemovingRow] = useState({});
    const cart = useSelector((state) => state.cart);
    const [orderRows, setOrderRows] = useState(cart?.orderRows);
    const { discountRows } = cart;
    const deliveryPrice = useSelector(state => state.checkout.payload.selectedDeliveryMethod.price);

    useEffect(() => {
        dispatch(fetchBudget())

        let totalExclVat = cart?.grandTotalExclVat;
        totalExclVat = totalExclVat.replace(/\s/g, '');
        let valueWithoutKr = totalExclVat.replace('kr', '');
        let floatValue = parseFloat(valueWithoutKr.replace(',', '.'));
        let newSum = floatValue - deliveryPrice;

        dispatch(validateCheckout(newSum))
    },[cart,deliveryPrice])

    useEffect(() => {
        setOrderRows(cart?.orderRows);
    }, [cart]);

    const removeOrderRow = useCallback(
        (articleNumber) =>
            dispatch(update(articleNumber, 0)).then(() =>
                dispatch(reloadPayment())
            ),
        [dispatch]
    );

    const handleQuantityInput = useCallback(
        (articleNumber, quantity, rowSystemId, ensureCorrectness = true) => {
            // In case of onBlur event, we need ensureCorrectness as true in order to ensure if the value is a valid float number
            // In case of onChange event, we accept invalid float number, but we don't send any request to the server.
            let floatQuantity = parseFloat(quantity);
            let validValue = !isNaN(floatQuantity) && floatQuantity > 0;
            if (ensureCorrectness) {
                quantity = validValue ? floatQuantity : 1;
                floatQuantity = quantity;
                validValue = true;
            }
            const index = orderRows.findIndex(
                (item) => item.rowSystemId === rowSystemId
            );
            const oldQuantity = parseFloat(orderRows[index].quantity);
            if (floatQuantity !== oldQuantity) {
                mapCartToAbort[articleNumber] &&
                    mapCartToAbort[articleNumber].abort();
                quantityDispatch && quantityDispatch.cancel();
                validValue &&
                    quantityDispatch(articleNumber, quantity, dispatch);
            }
            const tempOrderRows = [...orderRows];
            tempOrderRows[index] = {
                ...tempOrderRows[index],
                quantity,
            };
            setOrderRows(tempOrderRows);
        },
        [dispatch, orderRows]
    );

    const ProductImage = useCallback(({ order }) => {
        return (
            <a href={order.url}>
                <img
                    className="checkout-cart__image"
                    src={order.image}
                    alt={order.name}
                />
            </a>
        );
    }, []);

    const ProductName = useCallback(({ order }) => {
        return (
            <Fragment>
                <a href={order.url}>
                    <div className="checkout-cart__brand-name"> {order.brand}</div>
                    <div className="checkout-cart__product-name"> {order.name}</div>
                    {order.color ? (
                    <div className="checkout-cart__filter-container">
                        <span className="checkout-cart__filter-name"> {translate("code.checkout.cart.header.color")}</span>
                        <span className="checkout-cart__filter-value"> {order.color}</span>
                    </div>
                    ):(
                        null
                    )}
                    {order.size ? (
                    <div className="checkout-cart__filter-container">
                        <span className="checkout-cart__filter-name"> {translate("code.checkout.cart.header.size")}</span>
                        <span className="checkout-cart__filter-value"> {order.size}</span>
                    </div>
                    ):(
                        null
                    )}
                </a>
            </Fragment>
        );
    }, []);

    const ProductPrice = useCallback(({ order }) => {
        return (
            <div className="checkout-cart__per-price">
                {order.isFreeGift ? order.campaignPrice : order.price}
            </div>
        );
    }, []);

    const ArticleNumber = useCallback(({ order }) => {
        return (
            <div className="checkout-cart__article-number">
                {order.articleNumber}
            </div>
        );
    }, []);

    const ProductQuantity = useCallback(
        (order) => {
            return (
                <Fragment>
                    {order.isFreeGift ? (
                        <div>{order.quantity}</div>
                    ) : (
                        <input
                            className="checkout-cart__input"
                            type="number"
                            min="1"
                            maxLength="3"
                            value={order.quantity.toString()}
                            onChange={(event) =>
                                handleQuantityInput(
                                    order.articleNumber,
                                    event.target.value,
                                    order.rowSystemId,
                                    false
                                )
                            }
                            onBlur={(event) =>
                                handleQuantityInput(
                                    order.articleNumber,
                                    event.target.value,
                                    order.rowSystemId
                                )
                            }
                        />
                    )}
                </Fragment>
            );
        },
        [handleQuantityInput]
    );

    const ProductTotalPrice = ({ order }) => {
        return (
            <div className="checkout-cart__total-price--box">
                {order.totalCampaignPrice ?(
                    <>
                        {order.isFreeGift &&(
                            order.totalCampaignPrice
                        )}
                        <span className="checkout-cart__total-price__prices">
                            {order.totalPrice}
                        </span> 
                        <span className="checkout-cart__total-price__prices">
                            {order.totalCampaignPrice}
                        </span>
                    </>
                ) :                 
                    <span>
                        {order.totalPrice}
                    </span>
                //JIC-312
                //     order.priceWithoutTierPrice ? (
                //     <>
                //         <span className="checkout-cart__total-price__prices">
                //             {order.priceWithoutTierPrice}
                //         </span>
                //         <span className="checkout-cart__total-price__prices">
                //             {order.totalPrice}
                //         </span> 
                //     </>
                // ) : (
                //     <span>
                //         {order.totalPrice}
                //     </span>
                // )
                }
            </div>
        );
    };

    const RemoveBtn = useCallback(
        ({ order }) => {
            return (
                <Fragment>
                    {!order.isFreeGift && !removingRow[order.rowSystemId] && (
                        <a
                            className="table__icon table__icon--delete"
                            onClick={() =>
                                setRemovingRow({
                                    ...removingRow,
                                    [order.rowSystemId]: true,
                                })
                            }
                            title={translate('general.remove')}
                        ></a>
                    )}
                    {!order.isFreeGift && removingRow[order.rowSystemId] && (
                        <Fragment>
                            <a
                                className="table__icon table__icon--accept"
                                onClick={() =>
                                    removeOrderRow(order.articleNumber)
                                }
                                title={translate('general.ok')}
                            ></a>
                            <a
                                className="table__icon table__icon--cancel"
                                onClick={() =>
                                    setRemovingRow({
                                        ...removingRow,
                                        [order.rowSystemId]: false,
                                    })
                                }
                                title={translate('general.cancel')}
                            ></a>
                        </Fragment>
                    )}
                </Fragment>
            );
        },
        [removeOrderRow, removingRow]
    );

    const CartTotal = useCallback(({ cart }) => {
        return (
            <Fragment>

                <div className="checkout-cart__total-price-text">
                    {translate('checkout.cart.total')}: 
                </div>
                <div className="checkout-cart__total-price-sum">
                    {cart.orderTotal}
                </div>
            </Fragment>
        );
    }, []);

    return (
        <div className="row checkout__container">
            <div className="small-12 simple-table hide-for-small-only hide-for-medium-only">
                <div className="row small-unstack no-margin row-header">
                    <div className="columns small-5 medium-2 large-1">
                    </div>
                    <div className="columns small-12 medium-4 large-3">
                        {translate('code.checkout.cart.header.productinformation')}
                    </div>
                    <div className="columns small-3 medium-2 large-3">
                        {translate('code.checkout.cart.header.articlenumber')}
                    </div>
                    <div className="columns small-3 medium-2 large-2">
                        {translate('checkout.cart.header.price')}
                    </div>
                    <div className="columns small-4 medium-2 large-2">
                        {translate('checkout.cart.header.quantity')}
                    </div>
                    <div className="columns small-5 medium-2 large-1">
                        {translate('checkout.cart.header.total')}
                    </div>
                </div>
                {orderRows.map((order, i, row) => (
                    <div
                        className={`row small-unstack no-margin checkout-cart__row ${i + 1 === row.length ? "checkout-cart__row-no-border" : ""}`}
                        key={order.rowSystemId}
                    >
                        <div className="columns small-3 medium-1 large-1 checkout-cart__bin">
                            <RemoveBtn order={order} />
                        </div>
                        <div className="columns small-12 medium-4 large-3 checkout-cart__image-container">
                            <div className="checkout-cart__image-wrapper">
                                <ProductImage order={order} />
                            </div>
                            <div className="checkout-cart__image-info">
                                <ProductName order={order} />
                            </div>
                        </div>
                        <div className="columns small-3 medium-2 large-3 simple-table__cell--no-break-word">
                            <ArticleNumber order={order} />
                        </div>
                        <div className="columns small-3 medium-2 large-2 simple-table__cell--no-break-word">
                            <ProductPrice order={order} />
                        </div>
                        <div className="columns small-2 medium-2 large-1">
                            {ProductQuantity(order)}
                        </div>
                        <div className="checkout-cart__total-price columns small-2 medium-3 large-2 simple-table__cell--no-break-word">
                            <ProductTotalPrice order={order} />
                        </div>
                    </div>
                ))}
                <div className="row small-unstack no-margin checkout-cart__row-total">
                    <CartTotal cart={cart} />
                </div>
            </div>
            <div className="small-12 simple-table checkout-mobile show-for-medium-only">
                {orderRows.map((order) => (
                    <div className="row no-margin" key={order.rowSystemId}>
                        <div className="columns small-3">
                            <ProductImage order={order} />
                        </div>
                        <div className="columns small-9">
                            <div className="row">
                                <div className="small-8 columns">
                                    <div className="flex-container flex-dir-column align-center">
                                        <ProductName order={order} />
                                    </div>
                                </div>
                                <div className="small-4 columns flex-container align-right">
                                    <RemoveBtn order={order} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="small-12 columns flex-container align-justify">
                                    <div className='checkout-cart__pre-price-container'>
                                        <ProductPrice order={order} />
                                    </div>
                                    <div>{ProductQuantity(order)}</div>
                                </div>
                            </div>
                            <div className="row checkout-cart__mobile-total-price">
                                <div className="checkout-cart__total-price small-12 columns text--right">
                                {translate('checkout.cart.header.total')}
                                    <ProductTotalPrice order={order} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="row no-margin">
                    <div className="columns mobile-checkout-total">
                        <CartTotal cart={cart} />
                    </div>
                </div>
            </div>
            <div className="small-12 simple-table checkout-mobile show-for-small-only">
                {orderRows.map((order) => (
                    <div className="row no-margin" key={order.rowSystemId}>
                        <div className="columns small-3">
                            <ProductImage order={order} />
                        </div>
                        <div className="columns small-9">
                            <div className="row">
                                <div className="small-8 columns">
                                    <div className="flex-container flex-dir-column align-center">
                                        <ProductName order={order} />
                                    </div>
                                </div>
                                <div className="small-4 columns flex-container align-right">
                                    <RemoveBtn order={order} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="small-12 columns flex-container align-justify">
                                    <div className='checkout-cart__pre-price-container'>
                                        <ProductPrice order={order} />
                                    </div>
                                    <div>{ProductQuantity(order)}</div>
                                </div>
                            </div>
                            <div className="row checkout-cart__mobile-total-price">
                                <div className="checkout-cart__total-price small-12 columns text--right">
                                {translate('checkout.cart.header.total')}
                                    <ProductTotalPrice order={order} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="row no-margin">
                    <div className="columns mobile-checkout-total">
                        <CartTotal cart={cart} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Cart;
